import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, PenLine, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import api, { formatApiError } from "@/lib/api";

const TYPE_LABELS = {
  participation: "Participation in Independent Travel Training",
  parent_communication: "Parent/Carer communication",
  information_sharing: "Relevant information sharing",
  photography: "Photography (identification)",
  training_evidence_photos: "Training evidence photographs",
  emergency_contact: "Emergency contact",
  council_info_sharing: "Local Authority information sharing",
  school_info_sharing: "School information sharing",
};

const STATUS_LABELS = {
  pending: "Pending", obtained: "Obtained", not_required: "Not Required",
  declined: "Declined", withdrawn: "Withdrawn", expired: "Expired",
};

const METHOD_LABELS = {
  signed_form: "signed form",
  verbal_recorded: "verbal (recorded)",
  email: "email",
  council_authorisation: "council authorisation",
  onscreen_signature: "on-screen signature",
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-transparent",
  obtained: "bg-ukgreen-light text-ukgreen border-transparent",
  not_required: "bg-slate-100 text-slate-600 border-transparent",
  declined: "bg-red-50 text-red-700 border-transparent",
  withdrawn: "bg-red-50 text-red-700 border-transparent",
  expired: "bg-slate-100 text-slate-600 border-transparent",
};

const EMPTY = {
  type: "participation", status: "pending", provided_by_name: "", provided_by_relationship: "",
  date_provided: "", method: "", review_date: "", notes: "",
};

export default function ConsentsTab({ child }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("consents:manage");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sigData, setSigData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/children/${child.id}/consents`);
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      type: c.type, status: c.status, provided_by_name: c.provided_by_name || "",
      provided_by_relationship: c.provided_by_relationship || "", date_provided: c.date_provided || "",
      method: c.method || "", review_date: c.review_date || "", notes: c.notes || "",
    });
    setSigData(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (sigData) {
        const blob = await (await fetch(sigData)).blob();
        const fd = new FormData();
        fd.append("file", new File([blob], "onscreen-signature.png", { type: "image/png" }));
        fd.append("category", "consent_document");
        fd.append("child_id", child.id);
        fd.append("description", `On-screen signature — ${form.provided_by_name || "parent/carer"}`);
        const { data: sigDoc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        payload.document_id = sigDoc.id;
        payload.method = "onscreen_signature";
      }
      if (editing) {
        await api.patch(`/children/${child.id}/consents/${editing.id}`, payload);
        toast.success(payload.status === "withdrawn" ? "Consent withdrawn" : "Consent updated");
      } else {
        await api.post(`/children/${child.id}/consents`, payload);
        toast.success("Consent recorded");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (c) => {
    try {
      await api.patch(`/children/${child.id}/consents/${c.id}`, {
        type: c.type, status: "withdrawn",
        provided_by_name: c.provided_by_name, provided_by_relationship: c.provided_by_relationship,
        date_provided: c.date_provided, method: c.method, review_date: c.review_date, notes: c.notes,
      });
      toast.success("Consent withdrawn — recorded with date and audit entry");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadForm = async () => {
    try {
      const r = await api.get(`/children/${child.id}/consents/form.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${child.case_id || "child"}-parental-consent-form.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="consents-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500 max-w-xl">
          Consent records show operational status only. Lawful-basis decisions remain with authorised organisational
          staff. Consent records are never deleted — withdrawal is recorded instead.
        </p>
        <div className="flex gap-2 shrink-0">
          {items.length > 0 && (
            <Button variant="outline" className="h-10" data-testid="consent-form-pdf-button" onClick={downloadForm}>
              <PenLine className="h-4 w-4 mr-2" /> Signed consent form (PDF)
            </Button>
          )}
          {canManage && (
            <Button data-testid="consent-add-button" className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold"
              onClick={() => { setEditing(null); setForm(EMPTY); setSigData(null); setOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" /> Record consent
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-600" data-testid="consents-empty">No consent records yet.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="consents-list">
          {items.map((c) => (
            <Card key={c.id} data-testid={`consent-record-${c.id}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900 text-sm">{TYPE_LABELS[c.type] || c.type}</p>
                    <Badge className={STATUS_STYLES[c.status]} data-testid={`consent-status-${c.id}`}>{STATUS_LABELS[c.status] || c.status}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {c.provided_by_name ? `Provided by ${c.provided_by_name}` : "Provider not recorded"}
                    {c.provided_by_relationship ? ` (${c.provided_by_relationship})` : ""}
                    {c.date_provided ? ` · ${c.date_provided}` : ""}
                    {c.method ? ` · via ${METHOD_LABELS[c.method] || c.method}` : ""}
                    {c.review_date ? ` · review by ${c.review_date}` : ""}
                    {c.withdrawn_at ? ` · withdrawn ${new Date(c.withdrawn_at).toLocaleDateString("en-GB")}` : ""}
                  </p>
                  {c.notes && <p className="text-xs text-slate-600 mt-1">{c.notes}</p>}
                  {c.document_id && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.document_id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`consent-signature-${c.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      <PenLine className="h-3.5 w-3.5" /> View signature document
                    </a>
                  )}
                </div>
                {canManage && (
                  <div className="flex gap-2">
                    <Button variant="outline" className="h-9 text-xs" data-testid={`consent-edit-${c.id}`} onClick={() => openEdit(c)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    {c.status !== "withdrawn" && (
                      <Button variant="outline" className="h-9 text-xs border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`consent-withdraw-${c.id}`} onClick={() => withdraw(c)}>
                        Withdraw
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit consent record" : "Record consent / authority"}</DialogTitle>
            <DialogDescription>Each consent type is recorded separately with its own status.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="consent-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Consent / authority type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="consent-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`consent-type-${k}`}>{l}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="consent-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`consent-status-option-${k}`}>{l}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Provided by (name)</Label>
                <Input data-testid="consent-provided-by-input" value={form.provided_by_name} onChange={(e) => setForm({ ...form, provided_by_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Relationship / authority</Label>
                <Input data-testid="consent-relationship-input" value={form.provided_by_relationship} onChange={(e) => setForm({ ...form, provided_by_relationship: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Date provided</Label>
                <Input data-testid="consent-date-input" type="date" value={form.date_provided} onChange={(e) => setForm({ ...form, date_provided: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Method</Label>
                <Select value={form.method || "none"} onValueChange={(v) => setForm({ ...form, method: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="consent-method-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="signed_form">Signed form</SelectItem>
                    <SelectItem value="verbal_recorded">Verbal (recorded)</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="council_authorisation">Council authorisation</SelectItem>
                    <SelectItem value="onscreen_signature">On-screen signature</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Expiry / review date</Label>
                <Input data-testid="consent-review-input" type="date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea data-testid="consent-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="space-y-1.5" data-testid="consent-signature-block">
              <Label>On-screen signature (optional)</Label>
              <p className="text-xs text-slate-500">
                The person giving consent can sign below. Stored as a private, access-controlled document linked to this consent record.
              </p>
              <SignaturePad onChange={setSigData} />
              {sigData && <p className="text-xs font-medium text-emerald-700" data-testid="consent-signature-captured">Signature captured</p>}
            </div>
            <DialogFooter>
              <Button data-testid="consent-save-button" type="submit" disabled={saving}
                className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Record consent"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
